import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import { SX, LEVELS } from './leaderboardData';

/**
 * The nine-tier level ladder as a reference strip, so a mentor reading the
 * standings can see what a level name is worth without opening the docs.
 * `reached` (a level key) marks how far the batch's leader has climbed —
 * everything up to it reads as earned, the rest as still ahead.
 */
export default function LevelLadder({ reached }) {
  const reachedIndex = LEVELS.findIndex((l) => l.key === reached);

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: `${SX.radius.lg}px`,
        background: SX.card,
        border: `1px solid ${SX.borderStrong}`,
        boxShadow: SX.shadowSoft,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Icon icon="solar:medal-star-bold-duotone" width={18} height={18} color={SX.purple} />
        <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: SX.text }}>Level ladder</Typography>
        <Typography sx={{ fontSize: 11.5, color: SX.textMuted }}>
          — derived from XP; every learner climbs the same nine tiers
        </Typography>
      </Stack>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {LEVELS.map((level, i) => {
          const earned = reachedIndex >= 0 && i <= reachedIndex;
          return (
            <Tooltip key={level.key} arrow title={`${level.min.toLocaleString()} XP and above`}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                sx={{
                  px: 1.1,
                  py: 0.6,
                  borderRadius: 999,
                  cursor: 'default',
                  background: earned ? `${level.color}14` : 'rgba(27,36,56,0.03)',
                  border: `1px solid ${earned ? `${level.color}38` : SX.border}`,
                  color: earned ? level.color : SX.textMuted,
                  opacity: reachedIndex >= 0 && !earned ? 0.75 : 1,
                  transition: 'transform .15s ease',
                  '&:hover': { transform: 'translateY(-2px)' },
                }}
              >
                <Icon icon={level.icon} width={15} height={15} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 800 }}>{level.name}</Typography>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, opacity: 0.75 }}>
                  {level.min.toLocaleString()}+
                </Typography>
              </Stack>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}
